import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { deviceSettingsQuery, nestedTableQuery, post, printerSettingsQuery, summaryQuery } from '../api/printeeng'
import type { Period } from '../api/securitee'
import { ApiError } from '../api/client'
import { escapeHtml } from '../inventoree/format'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { NestedTable, type NestedColumn, type NestedState } from '../securitee/NestedTable'
import { SettingsCheck, SettingsRadio } from '../ui/SettingsParts'

/**
 * Randul desfasurat din Printeeng - `getActionTabs` (masina) si
 * `getPrintersActionTabs` (imprimanta): Summary, Files, Events, Settings, cu
 * perioada listei (`from`/`to`) dusa in Files.
 */
const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'files', label: 'Files' },
  { key: 'events', label: 'Events' },
  { key: 'settings', label: 'Settings' },
]

export function PrinteengTabs({ kind, id, period }: { kind: 'devices' | 'printers'; id: number; period: Period }) {
  const [tab, setTab] = useState('summary')
  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {TABS.map((t) => (
            <li key={t.key}>
              <a href="#" className={`action_button${tab === t.key ? ' active' : ''}`} onClick={(e) => { e.preventDefault(); setTab(t.key) }}>{t.label}</a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'summary' && <SummaryTab kind={kind} id={id} />}
          {tab === 'files' && <TableTab kind={kind} id={id} what="files" columns={kind === 'devices' ? DEVICE_FILE_COLUMNS : PRINTER_FILE_COLUMNS} period={period} />}
          {tab === 'events' && <TableTab kind={kind} id={id} what="events" columns={EVENT_COLUMNS} />}
          {tab === 'settings' && (kind === 'devices' ? <DeviceSettingsTab id={id} /> : <PrinterSettingsTab id={id} />)}
        </div>
      </div>
    </div>
  )
}

function Stat({ color, icon, number, label }: { color: string; icon: string; number: string | number; label: string }) {
  return (
    <div className="col-md-3">
      <div className={`dashboard-stat ${color}`}>
        <div className="visual"><i className={icon}></i></div>
        <div className="details"><div className="number">{number}</div><div className="desc"></div></div>
        <div className="more">{label}</div>
      </div>
    </div>
  )
}

/** devices/others/summary.php si printers/others/summary.php */
function SummaryTab({ kind, id }: { kind: 'devices' | 'printers'; id: number }) {
  const q = useQuery(summaryQuery(kind, id))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const s = q.data
  return (
    <>
      <div className="row">
        <Stat color="blue-madison" icon="fa fa-file" number={s.pages} label="Printed pages" />
        {kind === 'printers' ? <Stat color="blue-madison" icon="fa fa-money-bill" number={`${s.costs ?? 0} ${s.currency}`} label="Printing costs" /> : null}
        <Stat color="green-haze" icon="fa fa-cloud" number={s.co2} label="CO2 consumed" />
        <Stat color="green-haze" icon="fa fa-tree" number={s.cut_trees} label="Cut trees" />
      </div>
      {s.quota ? (
        <div className="row">
          <Stat color="blue-mauve" icon="fa fa-user" number={s.quota.profile} label="Quota / period" />
          <Stat color="blue-mauve" icon="fa fa-print" number={s.quota.printed} label="Printed this period" />
          <Stat color="blue-mauve" icon="fa fa-battery-half" number={s.quota.remained} label="Remaining pages" />
          <Stat color="blue-mauve" icon="fa fa-plus" number={s.quota.extra} label="Extra quota" />
        </div>
      ) : null}
      <div className="row">
        <div className="col-md-12 divider"><h4 className="bordered">Users Logged Summary</h4></div>
        <div className="col-md-12">
          <table className="table table-striped centered">
            <thead><tr><th>User Logged On</th><th>Pages</th><th>CO2 consumed</th><th>Cut Trees</th></tr></thead>
            <tbody>
              {s.loggedUserSummary.length === 0 ? (
                <tr><td>N/A</td><td>N/A</td><td>N/A</td><td>N/A</td></tr>
              ) : s.loggedUserSummary.map((u) => (
                <tr key={u.logged_user}><td>{u.logged_user}</td><td>{u.pages}</td><td>{u.co2}</td><td>{u.cut_trees}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

const DEVICE_FILE_COLUMNS: NestedColumn[] = [
  { key: 'logged_user', title: 'User Logged On', html: (r) => escapeHtml(r.logged_user) },
  { key: 'file_name', title: 'File name', html: (r) => escapeHtml(r.file_name) },
  { key: 'pages', title: 'Pages', html: (r) => escapeHtml(r.pages) },
  { key: 'cost', title: 'Cost', html: (r) => `${escapeHtml(r.cost ?? 0)} ${escapeHtml(r.currency)}` },
  { key: 'co2', title: 'CO2 consumed', html: (r) => escapeHtml(r.co2) },
  { key: 'printer_name', title: 'Printer', html: (r) => escapeHtml(r.printer_name) },
  { key: 'printing_type', title: 'Printing type', html: (r) => escapeHtml(r.printing_type) },
  { key: 'date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => escapeHtml(r.date) },
]

const PRINTER_FILE_COLUMNS: NestedColumn[] = [
  { key: 'logged_user', title: 'User Logged On', html: (r) => escapeHtml(r.logged_user) },
  { key: 'file_name', title: 'File name', html: (r) => escapeHtml(r.file_name) },
  { key: 'pages', title: 'Pages', html: (r) => escapeHtml(r.pages) },
  { key: 'co2', title: 'CO2 consumed', html: (r) => escapeHtml(r.co2) },
  { key: 'cut_trees', title: 'Cut Trees', html: (r) => escapeHtml(r.cut_trees) },
  { key: 'printing_type', title: 'Printing type', html: (r) => escapeHtml(r.printing_type) },
  { key: 'date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => escapeHtml(r.date) },
]

const EVENT_COLUMNS: NestedColumn[] = [
  { key: 'event_date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => escapeHtml(r.event_date) },
  { key: 'event_message', title: 'Message', html: (r) => escapeHtml(r.event_message) },
  { key: 'user_id', title: 'User', html: (r) => (r.Prenume ? escapeHtml(`${r.Prenume} ${r.Nume ?? ''}`) : '<strong>System</strong>') },
]

function TableTab({ kind, id, what, columns, period }: { kind: 'devices' | 'printers'; id: number; what: 'files' | 'events'; columns: NestedColumn[]; period?: Period }) {
  const last = columns.length - 1
  const [state, setState] = useState<NestedState>({ start: 0, length: 10, order: { column: last, dir: 'desc' }, search: '' })
  const col = columns[state.order.column] ?? columns[last]!
  const q = useQuery({
    ...nestedTableQuery(kind, id, what, { draw: 1, start: state.start, length: state.length, orderColumn: state.order.column, orderName: col.key, orderDir: state.order.dir, search: state.search, ...(period ?? {}) }),
    placeholderData: keepPreviousData,
  })
  return (
    <div className="row"><div className="col-md-12">
      <NestedTable id={`${kind}_${what}`} columns={columns} rows={q.data?.data ?? []} total={q.data?.recordsTotal ?? 0} fetching={q.isFetching} error={q.isError ? (q.error as Error).message : null} state={state} onState={setState} />
    </div></div>
  )
}

async function send(path: string, fields: Record<string, string>, onDone: () => void): Promise<void> {
  try {
    const body = await post(path, fields)
    toast(body.message, body.status === 'OK' ? 'Success' : 'Error', body.status === 'OK' ? 'success' : 'error')
    if (body.status === 'OK') onDone()
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
  }
}

/** devices/others/settings.php: profil, cota extra, blocare tiparire, mesaj agent. */
export function DeviceSettingsForm({ profiles, initial, submit, action, multi }: {
  profiles: { ID: number; name: string; quota: number; period: string }[]
  initial: { profile: number; block: number; agent_message: number; extra_quota: number | null } | null
  submit(fields: Record<string, string>): void
  action: string
  multi?: boolean
}) {
  return (
    <form className="form-horizontal tk-action-form device_settings" method="post" action={action} onSubmit={(e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const d = new FormData(e.currentTarget)
      const fields: Record<string, string> = { profile: String(d.get('profile') ?? '0') }
      if (d.get('block_printing')) fields.block_printing = '1'
      fields.agent_message = d.get('agent_message') ? '1' : '0'
      const extra = d.get('extra_quota')
      if (!multi && extra !== null && String(extra) !== '') fields.extra_quota = String(extra)
      submit(fields)
    }}>
      <div className="form-group">
        <label className="col-md-3 control-label">Profile</label>
        <div className="col-md-9">
          <select className="form-control" name="profile" defaultValue={String(initial?.profile ?? 0)}>
            <option value="0">Select...</option>
            {profiles.map((p) => <option key={p.ID} value={p.ID}>{p.name} ({p.quota} / {p.period})</option>)}
          </select>
        </div>
      </div>
      {!multi ? (
        <div className="form-group">
          <label className="col-md-3 control-label">Extra quota (pages)</label>
          <div className="col-md-9"><input type="number" min={0} className="form-control" name="extra_quota" defaultValue={initial?.extra_quota ?? ''} /></div>
        </div>
      ) : null}
      <div className="form-group">
        <label className="col-md-3 control-label">Block printing</label>
        <div className="col-md-9"><SettingsCheck aria-label="Block printing" name="block_printing" value="1" defaultChecked={(initial?.block ?? 0) === 1} /></div>
      </div>
      <div className="form-group">
        <label className="col-md-3 control-label">Agent message</label>
        <div className="col-md-9"><SettingsCheck aria-label="Agent message" name="agent_message" value="1" defaultChecked={(initial?.agent_message ?? 1) === 1} /></div>
      </div>
      <div className="form-group"><div className="col-md-9 col-md-offset-3"><button type="submit" className="btn btn-primary save_device_settings">Save</button></div></div>
    </form>
  )
}

function DeviceSettingsTab({ id }: { id: number }) {
  const client = useQueryClient()
  const q = useQuery(deviceSettingsQuery(id))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  return <DeviceSettingsForm profiles={q.data.profiles} initial={q.data} action="/printeeng/saveDeviceSettings" submit={(f) => void send(`/printeeng/devices/${id}/settings`, f, () => void client.invalidateQueries({ queryKey: ['printeeng'] }))} />
}

/** printers/others/settings.php: toner, pret pe pagina direct sau toner / pagini; Remove printer. */
export function PrinterSettingsForm({ initial, currency, submit, action }: {
  initial: { pricing_type: number; price_per_page: number; toner_type: string; toner_price: number; toner_pages: number } | null
  currency: string
  submit(fields: Record<string, string>): void
  action: string
}) {
  const [pricing, setPricing] = useState(initial?.pricing_type === 2 ? 2 : 1)
  return (
    <form className="form-horizontal tk-action-form printer_settings" method="post" action={action} onSubmit={(e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const d = new FormData(e.currentTarget)
      const fields: Record<string, string> = {}
      for (const k of ['toner_type', 'pricing_type', 'page_cost', 'toner_price', 'toner_pages']) fields[k] = String(d.get(k) ?? '')
      submit(fields)
    }}>
      <div className="form-group">
        <label className="col-md-3 control-label">Toner type</label>
        <div className="col-md-9">
          <select className="form-control" name="toner_type" defaultValue={initial?.toner_type ?? '1'}>
            <option value="1">Original</option><option value="2">Recharged</option><option value="3">Compatible</option>
          </select>
        </div>
      </div>
      <div className="form-group">
        <div className="col-md-9 col-md-offset-3">
          <SettingsRadio className="setup-costs" name="pricing_type" value="1" checked={pricing === 1} onChange={() => setPricing(1)} label="Setup cost based on cost/page" />
        </div>
      </div>
      <div className="form-group">
        <label className="col-md-3 control-label">Cost / Page ({currency})</label>
        <div className="col-md-9"><input type="number" step="0.001" min={0} className="form-control" name="page_cost" defaultValue={initial?.price_per_page ?? 0} disabled={pricing !== 1} /></div>
      </div>
      <div className="form-group">
        <div className="col-md-9 col-md-offset-3">
          <SettingsRadio className="setup-costs" name="pricing_type" value="2" checked={pricing === 2} onChange={() => setPricing(2)} label="Setup cost based on toner options" />
        </div>
      </div>
      <div className="form-group">
        <label className="col-md-3 control-label">Toner price ({currency})</label>
        <div className="col-md-9"><input type="number" step="0.01" min={0} className="form-control" name="toner_price" defaultValue={initial?.toner_price ?? 0} disabled={pricing !== 2} /></div>
      </div>
      <div className="form-group">
        <label className="col-md-3 control-label">Estimated no. of pages to print</label>
        <div className="col-md-9"><input type="number" step="1" min={1} className="form-control" name="toner_pages" defaultValue={initial?.toner_pages ?? 0} disabled={pricing !== 2} /></div>
      </div>
      <div className="form-group"><div className="col-md-9 col-md-offset-3"><button type="submit" className="btn btn-small btn-primary save_printer_settings">Save</button></div></div>
    </form>
  )
}

function PrinterSettingsTab({ id }: { id: number }) {
  const client = useQueryClient()
  const q = useQuery(printerSettingsQuery(id))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const refresh = () => void client.invalidateQueries({ queryKey: ['printeeng'] })
  return (
    <>
      <PrinterSettingsForm initial={q.data} currency={q.data.currency} action="/printeeng/savePrinterSettings" submit={(f) => void send(`/printeeng/printers/${id}/settings`, f, refresh)} />
      <button type="button" className="btn btn-danger btn-sm remove_printer" onClick={() => { if (confirm('Are you sure?')) void send(`/printeeng/printers/${id}/remove`, {}, refresh) }}>
        <i className="fas fa-trash"></i> Remove printer
      </button>
    </>
  )
}
